import { useCallback, useState } from 'react';
import styled from 'styled-components';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { Button } from 'components/Button/Button';
import { NotificationList } from 'components/NotificationList/NotificationList';
import { H4 } from 'components/Text/H4';
import { markNotificationsAsViewed } from 'modules/main/store/ducks/userNotifications.actions';
import { useMainDispatch } from 'modules/main/store/hooks/useMainDispatch';
import { useMainSelector } from 'modules/main/store/hooks/useMainSelector';
import { colors, gutterWidth, transition } from 'theme/theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { getMappedNotificationIds } from 'utils/userNotifications';

import { StaffNotificationsIcon } from './StaffNotificationsIcon';

export const StaffMobileNotificationsMenuContainer = styled.div<{
  $open: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;

  width: 100%;
  height: 100vh;
  transition: all ${transition};
  overflow-y: scroll;
  will-change: transform;
  background-color: ${colors.white};
  z-index: 104;

  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-100%)')};
`;

export const StaffMobileNotificationsMenuHeader = styled.div`
  align-items: center;
  background: ${colors.white};
  display: flex;
  justify-content: space-between;
  position: relative;
  padding: 0 ${gutterWidth / 2}px;
  height: 60px;
  width: 100%;
  border-bottom: 1px solid ${colors.selectionGrey};
`;

const StaffNotificationButton = styled.button`
  padding: 8px 16px;
  &:hover {
    color: ${colors.hoverBlue};
  }
`;

export function StaffMobileNotificationsMenu() {
  const dispatch = useMainDispatch();

  const { notifications } = useMainSelector((state) => state.userNotifications);

  const [menuOpen, setMenuOpen] = useState(false);

  const handleView = useCallback(
    (viewedNotifications: (ApiNotification | MappedNotification)[]) =>
      dispatch(
        markNotificationsAsViewed(
          viewedNotifications.flatMap((notification) =>
            getMappedNotificationIds(notification),
          ),
        ),
      ),
    [dispatch],
  );

  const handleClick = useCallback(
    (viewedNotifications: (ApiNotification | MappedNotification)[]) => {
      setMenuOpen((open) => !open);

      trackClicked('Header Utility CTA - Notifications');

      if (!menuOpen && viewedNotifications.length) {
        handleView(viewedNotifications);

        trackEvent('Displayed User Menu Notifications', {
          notification_types: viewedNotifications.reduce(
            (acc, notification) =>
              // @ts-expect-error TS(2769): No overload matches this call.
              acc.includes(notification.type)
                ? acc
                : [...acc, notification.type],
            [],
          ),
        });
      }
    },
    [handleView, menuOpen],
  );

  return (
    <>
      <StaffNotificationButton
        aria-label={getText('Toggle notifications')}
        data-qa-id="notifications-menu-button"
        onClick={() => handleClick(notifications)}
      >
        <StaffNotificationsIcon notifications={notifications} id="mobile" />
      </StaffNotificationButton>

      <StaffMobileNotificationsMenuContainer
        $open={menuOpen}
        data-qa-id="mobile-notifications-menu"
      >
        <StaffMobileNotificationsMenuHeader>
          <H4 mb={0} color={colors.brandBlue}>
            {getText('Notifications')}
          </H4>
          <Button
            data-qa-id="close-mobile-notifications"
            onClick={() => setMenuOpen(false)}
            aria-label={getText('Close notifications')}
            variant="tertiary"
            noPadding
            icon={{ type: 'normalized', name: 'close' }}
          />
        </StaffMobileNotificationsMenuHeader>

        <NotificationList
          notifications={notifications}
          onClick={() => setMenuOpen(false)}
        />
      </StaffMobileNotificationsMenuContainer>
    </>
  );
}
